import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  PageHeader,
  StatusBadge,
  TableSkeleton,
  EmptyState,
} from "@/components/shared";
import { money, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Receipt, Plus, Send, CircleDollarSign, Ban, Shield } from "lucide-react";

const STATUS_TONE = {
  draft: "gray",
  issued: "blue",
  sent: "blue",
  due: "amber",
  overdue: "red",
  partially_paid: "amber",
  paid: "green",
  void: "gray",
};

export default function Invoices() {
  const { can, user } = useAuth();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [placements, setPlacements] = useState([]);
  const [createForm, setCreateForm] = useState({
    placement_id: "",
    payment_terms: "Net 30",
    tax_percent: "",
    notes: "",
  });
  const [payForm, setPayForm] = useState({
    amount: "",
    method: "bank_transfer",
    reference: "",
    note: "",
  });
  const [showPay, setShowPay] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [showVoid, setShowVoid] = useState(false);

  const financeAccess = can("revenue:view");

  const load = useCallback(async () => {
    try {
      const params = { page_size: 50 };
      if (filter !== "all") params.status = filter;
      const { data } = await api.get("/invoices", { params });
      setRows(data);
    } catch (e) {
      if (e?.response?.status === 403) setRows({ items: [], forbidden: true });
      else toast.error(apiErr(e));
      setRows({ items: [] });
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!showCreate) return;
    (async () => {
      try {
        const { data } = await api.get("/placements", { params: { page_size: 100 } });
        setPlacements(data.items || []);
      } catch {}
    })();
  }, [showCreate]);

  const refreshSelected = async (id) => {
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setSel(data);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const submitCreate = async () => {
    if (!createForm.placement_id) {
      toast.error("Pick a placement");
      return;
    }
    try {
      const body = {
        placement_id: createForm.placement_id,
        payment_terms: createForm.payment_terms,
        notes: createForm.notes || null,
      };
      if (createForm.tax_percent !== "")
        body.tax_percent = Number(createForm.tax_percent);
      const { data } = await api.post("/invoices", body);
      toast.success(`Draft invoice ${data.invoice_number} created`);
      setShowCreate(false);
      setCreateForm({
        placement_id: "",
        payment_terms: "Net 30",
        tax_percent: "",
        notes: "",
      });
      load();
      setSel(data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const doIssue = async () => {
    try {
      await api.post(`/invoices/${sel.id}/issue`, {});
      toast.success("Invoice issued");
      refreshSelected(sel.id);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };
  const doDeliver = async () => {
    try {
      await api.post(`/invoices/${sel.id}/deliver`, {
        method: "email",
        delivered_to: sel.client_name,
      });
      toast.success("Marked as delivered");
      refreshSelected(sel.id);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };
  const submitPayment = async () => {
    const amt = Number(payForm.amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a positive amount");
      return;
    }
    try {
      await api.post(`/invoices/${sel.id}/payments`, {
        amount: amt,
        method: payForm.method,
        reference: payForm.reference || null,
        note: payForm.note || null,
      });
      toast.success("Payment recorded");
      setShowPay(false);
      setPayForm({ amount: "", method: "bank_transfer", reference: "", note: "" });
      refreshSelected(sel.id);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };
  const submitVoid = async () => {
    if (!voidReason || voidReason.trim().length < 4) {
      toast.error("Please give a reason (≥ 4 chars)");
      return;
    }
    try {
      await api.post(`/invoices/${sel.id}/void`, { reason: voidReason });
      toast.success("Invoice voided");
      setShowVoid(false);
      setVoidReason("");
      refreshSelected(sel.id);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  if (rows?.forbidden || !financeAccess) {
    return (
      <div>
        <PageHeader
          title="Revenue & Invoices"
          subtitle="Finance-only workspace"
        />
        <Card className="p-8 text-center">
          <Shield className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
          <div className="font-medium">Restricted to Finance</div>
          <div className="text-sm text-muted-foreground">
            Invoice fees, payments and closure controls are not available to
            your role.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Revenue & Invoices"
        subtitle="Draft → Issued → Sent → Due → Paid · Guarantee & closure gate"
        actions={
          <div className="flex items-center gap-2">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="invoice-filter" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="issued">Issued</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="partially_paid">Partially Paid</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="void">Void</SelectItem>
              </SelectContent>
            </Select>
            <Button
              data-testid="new-invoice-btn"
              onClick={() => setShowCreate(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              New Invoice
            </Button>
          </div>
        }
      />

      <Card className="overflow-hidden">
        {!rows ? (
          <div className="p-4">
            <TableSkeleton />
          </div>
        ) : rows.items.length === 0 ? (
          <EmptyState
            icon={Receipt}
            title="No invoices"
            description="Create one from a confirmed placement."
          />
        ) : (
          <div className="overflow-x-auto scrollbar-thin">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Outstanding</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.items.map((inv) => (
                  <TableRow
                    key={inv.id}
                    data-testid={`invoice-row-${inv.id}`}
                    className="hover:bg-accent/40 cursor-pointer"
                    onClick={() => setSel(inv)}
                  >
                    <TableCell className="font-medium">
                      {inv.invoice_number}
                    </TableCell>
                    <TableCell className="text-sm">
                      {inv.client_name}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {inv.candidate_name}
                    </TableCell>
                    <TableCell>{money(inv.total, inv.currency)}</TableCell>
                    <TableCell>
                      {money(inv.outstanding_amount, inv.currency)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {fmtDate(inv.due_date)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge
                        value={inv.status}
                        tone={STATUS_TONE[inv.status] || "gray"}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          {sel && (
            <>
              <SheetHeader>
                <SheetTitle>{sel.invoice_number}</SheetTitle>
                <SheetDescription>
                  {sel.client_name} · {sel.candidate_name}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <Info label="Status">
                    <StatusBadge
                      value={sel.status}
                      tone={STATUS_TONE[sel.status] || "gray"}
                    />
                  </Info>
                  <Info label="Terms">{sel.payment_terms}</Info>
                  <Info label="Issue date">{fmtDate(sel.issue_date)}</Info>
                  <Info label="Due date">{fmtDate(sel.due_date)}</Info>
                  <Info label="Subtotal">
                    {money(sel.subtotal, sel.currency)}
                  </Info>
                  <Info label={`Tax (${sel.tax_percent}%)`}>
                    {money(sel.tax, sel.currency)}
                  </Info>
                  <Info label="Total">{money(sel.total, sel.currency)}</Info>
                  <Info label="Paid">
                    {money(sel.paid_amount, sel.currency)}
                  </Info>
                  <Info label="Outstanding">
                    {money(sel.outstanding_amount, sel.currency)}
                  </Info>
                </div>
                {sel.status !== "void" && (
                  <div className="flex flex-wrap gap-2 border-t pt-3">
                    {sel.status === "draft" && (
                      <Button
                        size="sm"
                        onClick={doIssue}
                        data-testid="inv-issue-btn"
                      >
                        Issue Invoice
                      </Button>
                    )}
                    {["issued", "due", "overdue"].includes(sel.status) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={doDeliver}
                        data-testid="inv-deliver-btn"
                      >
                        <Send className="h-4 w-4 mr-1" /> Mark Delivered
                      </Button>
                    )}
                    {sel.status !== "draft" && sel.status !== "paid" && (
                      <Button
                        size="sm"
                        onClick={() => setShowPay(true)}
                        data-testid="inv-record-payment-btn"
                      >
                        <CircleDollarSign className="h-4 w-4 mr-1" />
                        Record Payment
                      </Button>
                    )}
                    {sel.status !== "paid" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowVoid(true)}
                        data-testid="inv-void-btn"
                      >
                        <Ban className="h-4 w-4 mr-1" /> Void
                      </Button>
                    )}
                  </div>
                )}
                <div className="border-t pt-3">
                  <div className="text-xs font-medium mb-1">Payments</div>
                  {(sel.payments || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground">
                      No payments recorded yet.
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {sel.payments.map((p) => (
                        <div
                          key={p.id}
                          className="text-xs flex justify-between border rounded px-2 py-1"
                        >
                          <span>
                            {fmtDate(p.received_at)} · {p.method}
                            {p.reference ? ` · ${p.reference}` : ""}
                          </span>
                          <span className="font-medium">
                            {money(p.amount, sel.currency)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="border-t pt-3">
                  <div className="text-xs font-medium mb-1">History</div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {(sel.history || []).map((h, i) => (
                      <div
                        key={i}
                        className="text-xs text-muted-foreground"
                      >
                        <span className="font-medium text-foreground">
                          {h.state}
                        </span>{" "}
                        · {fmtDate(h.at)} · {h.by} — {h.note}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Invoice</DialogTitle>
            <DialogDescription>
              Pre-filled from the placement (fee, currency, agreement).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Placement</Label>
              <Select
                value={createForm.placement_id}
                onValueChange={(v) =>
                  setCreateForm({ ...createForm, placement_id: v })
                }
              >
                <SelectTrigger data-testid="new-invoice-placement">
                  <SelectValue placeholder="Pick a placement" />
                </SelectTrigger>
                <SelectContent>
                  {placements.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.candidate_name} · {p.client_name} ·{" "}
                      {money(p.placement_fee, p.currency)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Terms</Label>
              <Select
                value={createForm.payment_terms}
                onValueChange={(v) =>
                  setCreateForm({ ...createForm, payment_terms: v })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Net 15">Net 15</SelectItem>
                  <SelectItem value="Net 30">Net 30</SelectItem>
                  <SelectItem value="Net 45">Net 45</SelectItem>
                  <SelectItem value="Net 60">Net 60</SelectItem>
                  <SelectItem value="Net 90">Net 90</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tax %</Label>
              <Input
                type="number"
                placeholder="Leave blank for default"
                value={createForm.tax_percent}
                onChange={(e) =>
                  setCreateForm({ ...createForm, tax_percent: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                rows={2}
                value={createForm.notes}
                onChange={(e) =>
                  setCreateForm({ ...createForm, notes: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button onClick={submitCreate} data-testid="new-invoice-submit">
              Create Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPay} onOpenChange={setShowPay}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>
              {sel && (
                <span>
                  Outstanding: {money(sel.outstanding_amount, sel.currency)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                data-testid="pay-amount"
                value={payForm.amount}
                onChange={(e) =>
                  setPayForm({ ...payForm, amount: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Method</Label>
              <Select
                value={payForm.method}
                onValueChange={(v) => setPayForm({ ...payForm, method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="wire">Wire</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference</Label>
              <Input
                value={payForm.reference}
                onChange={(e) =>
                  setPayForm({ ...payForm, reference: e.target.value })
                }
              />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea
                rows={2}
                value={payForm.note}
                onChange={(e) =>
                  setPayForm({ ...payForm, note: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPay(false)}>
              Cancel
            </Button>
            <Button onClick={submitPayment} data-testid="pay-submit">
              Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showVoid} onOpenChange={setShowVoid}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Void Invoice</DialogTitle>
            <DialogDescription>
              This cannot be reversed. A reason is required and is audit-logged.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="void-reason"
            rows={3}
            placeholder="Reason (min 4 chars)"
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVoid(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitVoid}
              data-testid="void-submit"
            >
              Void
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, children }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{children}</div>
    </div>
  );
}
